import { CertificateType, SslType } from "../../../../utils/constants";

/** Classic SSLConfiguration cert-ready: host present or Universal (Sense Guard) type. */
export function isSslCertificateReady(sslConfig) {
  if (!sslConfig || Array.isArray(sslConfig)) return false;
  return !!sslConfig?.certs?.host?.length || sslConfig?.certs?.type === CertificateType.SENSE_GUARD;
}

/** Classic FULL_STRICT origin cert gate. */
export function isOriginCertificateReady(sslConfig) {
  if (!sslConfig || Array.isArray(sslConfig)) return false;
  return !!sslConfig?.sg_certs?.host && !!sslConfig?.sg_certs?.validTo;
}

/**
 * Install Continue block reason (classic continueToWAF messages).
 * @returns {string|null} error message or null when Continue is allowed
 */
export function getInstallContinueBlockReason(sslConfig, sslType) {
  const effectiveSslType = sslConfig?.ssl_type ?? sslType;
  if (effectiveSslType !== SslType.OFF && !isSslCertificateReady(sslConfig)) {
    return "Please verify domain and install SSL certificate";
  }
  if (effectiveSslType === SslType.FULL_STRICT && !isOriginCertificateReady(sslConfig)) {
    return "Please generate SSL origin certificates";
  }
  return null;
}

export function shouldSkipInstallForInsecure(sslType) {
  return sslType === SslType.OFF;
}

/** Config Process completion — live ssl/waf required (not create-only). */
export function isConfigProcessComplete({ siteCreatedThisSession, sslConfig, wafConfig, sslType }) {
  if (!siteCreatedThisSession) return false;
  if (!sslConfig || Array.isArray(sslConfig)) return false;
  if (!wafConfig || Array.isArray(wafConfig)) return false;
  const configProcessSslType = sslConfig?.ssl_type ?? sslType;
  const configProcessSslReady =
    configProcessSslType === SslType.OFF ||
    (isSslCertificateReady(sslConfig) &&
      (configProcessSslType !== SslType.FULL_STRICT || isOriginCertificateReady(sslConfig)));
  return Boolean(configProcessSslReady);
}

export function isConfigProcessFetchFailed(sslConfig, wafConfig, siteUid) {
  return Boolean(siteUid && (Array.isArray(sslConfig) || Array.isArray(wafConfig)));
}

/** Upload PEM pre-check before uploadCert API. */
export function getUploadPemBlockReason(fullchain, privkey) {
  if (!String(fullchain || "").trim() || !String(privkey || "").trim()) {
    return "Please paste or upload both fullchain and private key PEM files";
  }
  return null;
}

/** Persist HTTPS redirect only when draft differs from loaded baseline. */
export function shouldPersistHttpsRedirect(baselineEnabled, draftEnabled) {
  return Boolean(baselineEnabled) !== Boolean(draftEnabled);
}

/**
 * Universal CNAME records are usable only when both Name and Value are non-empty.
 * `[undefined, undefined]` has length 2 but must not count as ready (blocks silent empty UI).
 */
export function hasCnameValidationRecords(cnameValidations) {
  if (!Array.isArray(cnameValidations) || cnameValidations.length < 2) return false;
  return Boolean(String(cnameValidations[0] || "").trim() && String(cnameValidations[1] || "").trim());
}

/** Sites list: missing onboarded_at means Add Domain 2 Finish (`applyConfig`) never ran. */
export function isSiteSetupIncomplete(onboardedAt) {
  return onboardedAt == null || onboardedAt === "";
}

/** Non-empty wizard_step means setup is still in progress (Resume), even if onboarded_at was stamped early. */
export function hasActiveWizardStep(wizardStep) {
  return wizardStep != null && wizardStep !== "";
}

/**
 * Incomplete when wizard_step is set, or when onboarded_at is explicitly null/empty.
 * Unknown (rich GET /site with no merge, failed basis_all) → not incomplete (keep kebab).
 * Soft-deleted rows keep the kebab.
 */
export function isSiteRowSetupIncomplete(site, onboardedAtById = {}) {
  if (!site || site.isDeleted) return false;
  if (hasActiveWizardStep(site.wizard_step ?? site.setup_step)) return true;
  const fromSite = Object.prototype.hasOwnProperty.call(site, "onboarded_at");
  const idKey = site.id == null ? "" : String(site.id);
  const fromMap = Boolean(idKey) && Object.prototype.hasOwnProperty.call(onboardedAtById, idKey);
  if (!fromSite && !fromMap) return false;
  const value = fromSite ? site.onboarded_at : onboardedAtById[idKey];
  return isSiteSetupIncomplete(value);
}

/**
 * Header GlobalSiteSelector: hide sites that would show Resume setup
 * (incomplete Add Domain 2 — active wizard_step or null onboarded_at).
 */
export function isSiteEligibleForGlobalSelector(site) {
  if (!site?.site_id) return false;
  if (site.isDeleted === true || site.deleted) return false;
  return !isSiteRowSetupIncomplete(site);
}

/** Map site id → onboarded_at only when basis_all explicitly included the field. */
export function buildOnboardedAtById(siteList) {
  const map = {};
  (Array.isArray(siteList) ? siteList : []).forEach((s) => {
    if (s?.id != null && Object.prototype.hasOwnProperty.call(s, "onboarded_at")) {
      map[String(s.id)] = s.onboarded_at;
    }
  });
  return map;
}

/** Sites Action cell: resume button, empty (read-only incomplete), or kebab. */
export function getSiteRowActionKind(site, onboardedAtById = {}, canWrite = false) {
  if (isSiteRowSetupIncomplete(site, onboardedAtById)) {
    return canWrite ? "resume" : "none";
  }
  return "kebab";
}

/** Last Add Domain 2 page (Review). WI is 0. */
export const WIZARD_LAST_STEP = 5;

/** DB / API enum for Site.wizard_step (matches Prisma SiteWizardStep). */
export const SiteWizardStep = Object.freeze({
  WEBSITE_INFORMATION: "WEBSITE_INFORMATION",
  SSL_CONFIGURATION: "SSL_CONFIGURATION",
  INSTALL_SSL: "INSTALL_SSL",
  WAF_CONFIGURATION: "WAF_CONFIGURATION",
  CONFIGURATION_PROCESS: "CONFIGURATION_PROCESS",
  REVIEW: "REVIEW",
});

const INDEX_TO_ENUM = Object.freeze([
  SiteWizardStep.WEBSITE_INFORMATION,
  SiteWizardStep.SSL_CONFIGURATION,
  SiteWizardStep.INSTALL_SSL,
  SiteWizardStep.WAF_CONFIGURATION,
  SiteWizardStep.CONFIGURATION_PROCESS,
  SiteWizardStep.REVIEW,
]);

const ENUM_TO_INDEX = Object.freeze(
  Object.fromEntries(INDEX_TO_ENUM.map((name, index) => [name, index]))
);

/**
 * Value written to Site.wizard_step. After create, WI becomes SSL_CONFIGURATION.
 * @returns {string} SiteWizardStep enum name
 */
export function persistableWizardStep(wizardStepIndex) {
  const n = Number(wizardStepIndex);
  if (!Number.isInteger(n) || n <= 0) return SiteWizardStep.SSL_CONFIGURATION;
  if (n >= WIZARD_LAST_STEP) return SiteWizardStep.REVIEW;
  return INDEX_TO_ENUM[n];
}

/**
 * Persist only when the destination is ahead of the session high-water mark.
 * Next after Back must not shrink Resume — Back never lowers the high-water.
 * @param {number} toIndex destination UI wizard step
 * @param {number} highWaterIndex furthest step already visited/persisted this session
 */
export function shouldPersistWizardStepOnNavigate(toIndex, highWaterIndex) {
  const to = Number(toIndex);
  const high = Number(highWaterIndex);
  if (!Number.isInteger(to) || !Number.isInteger(high)) return false;
  return to > high;
}

/**
 * On close/discard: write only if the candidate step is ahead of the already-saved resume point.
 * Prevents Back-then-Close from overwriting a further wizard_step.
 */
export function shouldPersistWizardStepOnClose(candidateIndex, savedWizardStep) {
  const candidate = normalizeSavedWizardStep(persistableWizardStep(candidateIndex));
  if (candidate == null) return false;
  const saved = normalizeSavedWizardStep(savedWizardStep);
  if (saved == null) return true;
  return candidate > saved;
}

/**
 * Saved DB/API wizard_step → UI index (1–5), or null when missing/invalid (use SSL inference).
 * Accepts enum names and legacy integer indexes.
 */
export function normalizeSavedWizardStep(value) {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    const key = value.trim().toUpperCase();
    if (Object.prototype.hasOwnProperty.call(ENUM_TO_INDEX, key)) {
      const idx = ENUM_TO_INDEX[key];
      return idx <= 0 ? 1 : idx;
    }
    const asNumber = Number(value);
    if (Number.isInteger(asNumber)) return normalizeSavedWizardStep(asNumber);
    return null;
  }

  if (typeof value === "number" && Number.isInteger(value)) {
    if (value <= 0) return 1;
    if (value >= WIZARD_LAST_STEP) return WIZARD_LAST_STEP;
    return value;
  }

  return null;
}

/** @deprecated use persistableWizardStep — kept for any leftover imports */
export function persistableSetupStep(wizardStepIndex) {
  return persistableWizardStep(wizardStepIndex);
}

/** @deprecated use normalizeSavedWizardStep */
export function normalizeSavedSetupStep(value) {
  return normalizeSavedWizardStep(value);
}

/**
 * Resume Add Domain 2 after create. WI (0) is skipped.
 * SSL default OFF → SSL (1). Encrypted + certs blocked → Install (2). Encrypted + ready → WAF (3).
 * Used only when wizard_step is not saved on the site.
 */
export function getResumeWizardStep(sslConfig, sslType = SslType.OFF) {
  const type =
    sslConfig && !Array.isArray(sslConfig) && typeof sslConfig.ssl_type === "number"
      ? sslConfig.ssl_type
      : sslType;
  if (type !== SslType.OFF && getInstallContinueBlockReason(sslConfig, type)) {
    return 2;
  }
  if (type !== SslType.OFF) {
    return 3;
  }
  return 1;
}

/** Prefer the saved wizard page (enum or legacy int); otherwise infer from SSL. */
export function resolveResumeWizardStep(savedWizardStep, sslConfig, sslType = SslType.OFF) {
  const saved = normalizeSavedWizardStep(savedWizardStep);
  if (saved != null) return saved;
  return getResumeWizardStep(sslConfig, sslType);
}

/**
 * First paint for Resume: use list/DB wizard_step immediately so the modal does not
 * flash SSL (1) then jump to an inferred page (e.g. WAF). Defaults to SSL only when unset.
 */
export function getInitialResumeWizardStep(resumeSite) {
  return (
    normalizeSavedWizardStep(resumeSite?.wizard_step ?? resumeSite?.setup_step) ?? 1
  );
}

/**
 * Resume click: prefer any non-null wizard_step from basis_all or the rich row.
 * Do not let an explicit null on one source wipe a real value on the other.
 */
export function mergeResumeSiteFromList(site, siteList) {
  if (!site) return site;
  const fromList = (Array.isArray(siteList) ? siteList : []).find((s) => String(s.id) === String(site.id));
  const wizard_step =
    [fromList?.wizard_step, site.wizard_step, fromList?.setup_step, site.setup_step].find(
      (v) => v != null && v !== ""
    ) ?? null;
  const onboarded_at = fromList && Object.prototype.hasOwnProperty.call(fromList, "onboarded_at")
    ? fromList.onboarded_at
    : site.onboarded_at;
  return {
    ...site,
    wizard_step,
    onboarded_at,
  };
}

/**
 * Final resume page: live GET wizard_step, then list value, then SSL inference.
 * Never let a missing live field erase a list-saved step (that caused SSL flash → inferred WAF).
 */
export function resolveResumeWizardStepAfterHydrate(liveWizardStep, listWizardStep, sslConfig, sslType = SslType.OFF) {
  const saved =
    normalizeSavedWizardStep(liveWizardStep) ?? normalizeSavedWizardStep(listWizardStep);
  if (saved != null) return saved;
  return getResumeWizardStep(sslConfig, sslType);
}
